/**
 * useScoreGrowth - Hook for fetching growth chart data
 * Note: Performance metrics (rank, mindshare, score) come from useProfileInfo hook
 * This hook is primarily for fetching growth points for the chart
 */

import { useCallback, useEffect, useState } from 'react';
import { useApi } from './useApi';
import { getUserScoreGrowth } from '../api/services/userService';

/**
 * Hook to fetch user score growth data (for charts) by wallet address
 * @param {string} userAddress - User's wallet address
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Fetch immediately on mount (default: false)
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 * @returns {Object} - { graphData, loading, error, refetch }
 */
export const useScoreGrowth = (userAddress, options = {}) => {
  const {
    immediate = false,
    onSuccess,
    onError,
  } = options;
  const [shouldFetch, setShouldFetch] = useState(false);

  const fetchScoreGrowth = useCallback(
    () => getUserScoreGrowth(userAddress),
    [userAddress]
  );

  const { data, loading, error, execute, reset } = useApi(fetchScoreGrowth, {
    immediate: false,  // Don't use useApi's immediate, we'll control it
    initialData: null,
    onSuccess,
    onError,
  });
  
  // Trigger fetch when address becomes available
  useEffect(() => {
    if (userAddress && immediate) {
      execute();
    }
  }, [userAddress, immediate, execute]);

  // Format graph data from growth points
  // Expected API response format: { growthPoints: [{ score, date }, ...] }
  const graphData = data?.growthPoints ? data.growthPoints.map((point, index) => ({
    index,
    score: point.score || point,
    date: point.date || null,
  })) : [];

  return {
    graphData,
    loading,
    error,
    refetch: execute,
    reset,
  };
};

export default useScoreGrowth;
